/**
 * PublicInvestorPage — the Pollen Dots investor brief at /invest/:slug. NO AUTH.
 *
 * Designed in the pollendots.com brand language: warm paper (#FCF8F7), ink
 * (#030922), maroon (#581C2B), forest (#163224), petal pink (#F3BAA8),
 * Larken-style serif headlines (Fraunces), mono uppercase CTAs.
 *
 * Sections: hero (cover note) → live counters (no financials) → consolidated
 * journey atlas (every map feature: notes, Pollen Dots of Care halos, Open in
 * Maps) → the three decks with inline reading + download → footer.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Download, Loader2, X, ArrowRight } from 'lucide-react';
import PollenDotsMap from '@/components/PollenDotsMap';
import PollenDotsLegend from '@/components/PollenDotsLegend';
import { API } from '@/App';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

// ---- Brand tokens (sampled from pollendots.com) ----------------------------
const INK = '#030922';
const MAROON = '#581C2B';
const FOREST = '#163224';
const PETAL = '#F3BAA8';
const PAPER = '#FCF8F7';
const GRAY = '#787878';
const SERIF = "'Fraunces', Georgia, serif";
const MONO = "'JetBrains Mono', monospace";

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

// The pollen-petal mark from the pollendots.com CTA button.
const Petal = ({ size = 22, color = PETAL }) => (
  <svg width={size} height={size} viewBox="0 0 25 26" fill="none" aria-hidden="true">
    <g fill={color}>
      <path d="M14.4758 2.53867C14.4758 3.58046 13.8363 8.43343 12.5387 13.1492C11.2411 8.43343 10.6016 3.58046 10.6016 2.53867C10.6016 1.49688 11.4688 0.652344 12.5387 0.652344C13.6085 0.652344 14.4758 1.49688 14.4758 2.53867Z" />
      <path d="M21.1343 6.23903C20.2159 6.75992 16.2575 9.73047 12.749 13.1924C17.5551 11.9384 22.1531 10.056 23.0715 9.5351C23.9899 9.01421 24.3007 7.85409 23.7658 6.9439C23.2309 6.03372 22.0527 5.71814 21.1343 6.23903Z" />
      <path d="M21.1343 20.0856C20.2159 19.5647 16.2575 16.5941 12.749 13.1322C17.5551 14.3862 22.1531 16.2686 23.0715 16.7895C23.9899 17.3104 24.3007 18.4705 23.7658 19.3807C23.2309 20.2909 22.0527 20.6064 21.1343 20.0856Z" />
      <path d="M14.4758 23.7836C14.4758 22.7418 13.8363 17.8888 12.5387 13.173C11.2411 17.8888 10.6016 22.7418 10.6016 23.7836C10.6016 24.8254 11.4688 25.6699 12.5387 25.6699C13.6085 25.6699 14.4758 24.8254 14.4758 23.7836Z" />
      <g transform="scale(-1,1) translate(-25.1,0)">
        <path d="M21.1343 6.23903C20.2159 6.75992 16.2575 9.73047 12.749 13.1924C17.5551 11.9384 22.1531 10.056 23.0715 9.5351C23.9899 9.01421 24.3007 7.85409 23.7658 6.9439C23.2309 6.03372 22.0527 5.71814 21.1343 6.23903Z" />
        <path d="M21.1343 20.0856C20.2159 19.5647 16.2575 16.5941 12.749 13.1322C17.5551 14.3862 22.1531 16.2686 23.0715 16.7895C23.9899 17.3104 24.3007 18.4705 23.7658 19.3807C23.2309 20.2909 22.0527 20.6064 21.1343 20.0856Z" />
      </g>
    </g>
  </svg>
);

// Counts up to `value` on first scroll-into-view, and re-animates whenever the
// value changes (e.g., when switching between This Year / Last Year / Lifetime).
const CountUp = ({ value }) => {
  const ref = useRef(null);
  const visibleRef = useRef(false);
  const [display, setDisplay] = useState(0);

  // Track when the element is on-screen.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      visibleRef.current = e.isIntersecting;
    }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Animate from current `display` to the new `value` whenever it changes.
  useEffect(() => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return;
    if (!visibleRef.current && display === 0) {
      // Not yet on-screen and we haven't shown anything — wait for scroll.
      const el = ref.current;
      if (!el) return;
      const io = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) { io.disconnect(); animateTo(value); }
      }, { threshold: 0.35 });
      io.observe(el);
      return () => io.disconnect();
    }
    animateTo(value);
    function animateTo(target) {
      const from = display;
      const t0 = performance.now();
      const dur = 1100;
      const tick = (t) => {
        const p = Math.min(1, (t - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(Math.round(from + (target - from) * eased));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return <span ref={ref}>{display.toLocaleString('en-IN')}</span>;
};

// ---- Full-screen inline PDF reader ------------------------------------------
const PdfReader = ({ file, slug, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [width, setWidth] = useState(900);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false; let created = null;
    (async () => {
      try {
        const res = await fetch(`${API}/public/investor/${slug}/file/${file.id}`);
        if (!res.ok) throw new Error('fetch failed');
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        created = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
        setBlobUrl(created);
      } catch { if (!cancelled) onClose(); }
    })();
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [slug, file.id, onClose]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 900;
      setWidth(Math.min(1100, Math.max(300, Math.floor(w - 16))));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#F2EAE4' }} data-testid="pdf-reader-overlay">
      <div className="flex items-center gap-3 px-4 py-2.5 border-b sticky top-0"
        style={{ background: PAPER, borderColor: '#E8DCD3' }}>
        <Petal size={18} color={MAROON} />
        <p className="flex-1 text-sm truncate" style={{ color: INK, fontFamily: SERIF, fontWeight: 500 }}>{file.title}</p>
        <a
          href={`${API}/public/investor/${slug}/file/${file.id}/download`}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[11px] uppercase text-white"
          style={{ background: MAROON, fontFamily: MONO, letterSpacing: '0.08em' }}
          data-testid="reader-download-btn"
        >
          <Download className="w-3.5 h-3.5" /> Download
        </a>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5" data-testid="reader-close-btn" aria-label="Close reader">
          <X className="w-5 h-5" style={{ color: INK }} />
        </button>
      </div>
      <div ref={containerRef} className="flex-1 overflow-y-auto px-2 py-4">
        {!blobUrl ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: MAROON }} />
          </div>
        ) : (
          <div className="mx-auto" style={{ maxWidth: width }}>
            <Document
              file={blobUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={<div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" style={{ color: MAROON }} /></div>}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <div key={i} className="mb-3 shadow-md rounded overflow-hidden bg-white">
                  <Page pageNumber={i + 1} width={width} renderTextLayer={false} renderAnnotationLayer={false} />
                </div>
              ))}
            </Document>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Mono pill CTA (pollendots.com button style) ----------------------------
const MonoBtn = ({ children, onClick, href, filled, testid }) => {
  const cls = 'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] uppercase transition-transform duration-300 hover:-translate-y-0.5 cursor-pointer';
  const style = filled
    ? { background: MAROON, color: '#fff', border: `1px solid ${MAROON}`, fontFamily: MONO, letterSpacing: '0.1em' }
    : { background: '#fff', color: FOREST, border: `1px solid ${INK}`, fontFamily: MONO, letterSpacing: '0.1em' };
  if (href) {
    return <a href={href} className={cls} style={style} data-testid={testid}>{children}</a>;
  }
  return <button onClick={onClick} className={cls} style={style} data-testid={testid}>{children}</button>;
};

const SectionEyebrow = ({ children }) => (
  <p className="text-[10px] sm:text-[11px] uppercase mb-3 flex items-center gap-2"
    style={{ fontFamily: MONO, letterSpacing: '0.3em', color: MAROON }}>
    <Petal size={14} color={MAROON} /> {children}
  </p>
);

// ---- Period tabs (This Year / Last Year / Lifetime) ------------------------
const PERIODS = [
  { id: 'current_year', label: 'This Year' },
  { id: 'previous_year', label: 'Last Year' },
  { id: 'lifetime', label: 'Lifetime' },
];

const PeriodTabs = ({ value, onChange }) => (
  <div className="inline-flex rounded-full p-1 border" style={{ background: '#FDF6E6', borderColor: '#EBDFD6' }} data-testid="kpi-period-tabs">
    {PERIODS.map((p) => {
      const active = p.id === value;
      return (
        <button
          key={p.id}
          onClick={() => onChange(p.id)}
          data-testid={`kpi-period-${p.id}`}
          className="px-3 sm:px-4 py-1.5 text-[11px] sm:text-xs uppercase rounded-full transition-colors"
          style={{
            fontFamily: MONO,
            letterSpacing: '0.12em',
            background: active ? '#fff' : 'transparent',
            color: active ? MAROON : GRAY,
            border: active ? '1px solid #EBDFD6' : '1px solid transparent',
            cursor: 'pointer',
          }}
        >
          {p.label}
        </button>
      );
    })}
  </div>
);

// ---- KPI Card — investor-page aesthetic --------------------------------------
const Stat = ({ value, label, sub, testid, animate = true }) => (
  <div data-testid={testid}>
    <p className="text-3xl sm:text-4xl tabular-nums leading-none" style={{ fontFamily: SERIF, fontWeight: 500, color: FOREST }}>
      {value === null || value === undefined ? '—' :
        (animate && typeof value === 'number') ? <CountUp value={value} /> : value}
    </p>
    <p className="mt-2 text-[10px] uppercase" style={{ fontFamily: MONO, letterSpacing: '0.16em', color: GRAY }}>
      {label}
    </p>
    {sub && (
      <p className="mt-1 text-[11px]" style={{ color: '#9a8a80' }}>{sub}</p>
    )}
  </div>
);

const fmtLakhs = (v) => `\u20b9${((v || 0) / 100000).toFixed(2)}L`;
const fmtLakhsShort = (v) => `\u20b9${((v || 0) / 100000).toFixed(1)}L`;
const fmtRupees = (v) => `\u20b9${Math.round(v || 0).toLocaleString('en-IN')}`;

const KpiGrid = ({ kpis, loading }) => {
  if (!kpis) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-8 min-h-[160px] items-center">
        {loading
          ? Array.from({ length: 8 }).map((_, i) => (
            <Stat key={i} value="—" label="loading" animate={false} />
          ))
          : null}
      </div>
    );
  }
  const ue = kpis.unit_economics || {};
  const cacZero = (ue.blended_cac || 0) === 0;
  const totalRevenue = kpis.total_revenue || 0;

  return (
    <>
      {/* Row 1 — operational counters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-8" data-testid="kpi-row-counters">
        <Stat value={kpis.total_tours || 0} label="Tours" testid="inv-kpi-tours" />
        <Stat value={kpis.total_days || 0} label="Days" testid="inv-kpi-days" />
        <Stat value={kpis.unique_customers || 0} label="Customers" testid="inv-kpi-customers" />
        <Stat value={kpis.total_travellers || 0} label="Travellers" testid="inv-kpi-travellers" />
        <Stat value={fmtLakhsShort(totalRevenue)} label="Revenue" testid="inv-kpi-revenue" animate={false} />
        <Stat value={`${kpis.repeat_rate || 0}%`}
              label="Repeat customers"
              sub={`${kpis.repeat_customers || 0} of ${kpis.unique_customers || 0} customers`}
              testid="inv-kpi-repeat-customers" animate={false} />
        <Stat value={`${kpis.repeat_tour_rate || 0}%`}
              label="Repeat tours"
              sub={`${kpis.repeat_tours || 0} of ${kpis.total_tours || 0} tours`}
              testid="inv-kpi-repeat-tours" animate={false} />
        <Stat value={`${kpis.referral_revenue_pct || 0}%`}
              label="Referral revenue"
              sub={`${kpis.referral_customers_count || 0} of ${kpis.unique_customers || 0} customers`}
              testid="inv-kpi-referral-revenue" animate={false} />
      </div>

      {/* Row 2 — unit economics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-8 mt-10 pt-8 border-t" style={{ borderColor: '#EBDFD6' }} data-testid="kpi-row-economics">
        <Stat value={fmtLakhs(ue.avg_ltv)} label="Avg LTV"
              sub={`per customer · ${kpis.unique_customers || 0} customers`}
              testid="inv-kpi-ltv" animate={false} />
        <Stat value={cacZero ? fmtRupees(0) : fmtRupees(ue.blended_cac)}
              label="Blended CAC"
              sub={cacZero ? '100% organic / referral driven'
                : `${ue.direct_customers || 0} direct · ${ue.referred_customers || 0} referred`}
              testid="inv-kpi-cac" animate={false} />
        <Stat value={cacZero ? 'Organic' : `${ue.ltv_to_cac || 0}x`}
              label="LTV : CAC"
              sub={cacZero ? 'No paid spend · 100% word-of-mouth'
                : ((ue.ltv_to_cac || 0) >= 10 ? 'Elite · PMF proven'
                  : (ue.ltv_to_cac || 0) >= 3 ? 'Healthy' : 'Watch')}
              testid="inv-kpi-ltv-cac" animate={false} />
        <Stat value={fmtLakhs(ue.avg_ticket)} label="Avg ticket size"
              sub={`per tour · ${kpis.total_tours || 0} tours`}
              testid="inv-kpi-ticket" animate={false} />
      </div>
    </>
  );
};

const ReferrerRow = ({ rank, row, periodLabel }) => (
  <div className="flex items-center gap-4 py-3 border-b last:border-b-0" style={{ borderColor: '#EBDFD6' }}
       data-testid={`referrer-row-${rank}`}>
    <span className="text-[11px] uppercase tabular-nums w-8 shrink-0"
          style={{ fontFamily: MONO, letterSpacing: '0.16em', color: GRAY }}>
      {String(rank).padStart(2, '0')}
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-sm" style={{ color: INK, fontFamily: SERIF, fontWeight: 500 }}>
        {row.initials}
      </p>
      <p className="text-[11px] uppercase" style={{ fontFamily: MONO, letterSpacing: '0.12em', color: GRAY }}>
        Customer · {row.referred_count} referred · {row.tours} {row.tours === 1 ? 'tour' : 'tours'}
      </p>
    </div>
    <div className="text-right shrink-0">
      <p className="text-base tabular-nums" style={{ fontFamily: SERIF, fontWeight: 500, color: FOREST }}>
        {fmtLakhsShort(row.revenue)}
      </p>
      <p className="text-[10px] uppercase" style={{ fontFamily: MONO, letterSpacing: '0.16em', color: GRAY }}>
        {periodLabel}
      </p>
    </div>
  </div>
);

const ReferrersLeaderboard = ({ referrers, period }) => {
  const periodLabel = period === 'current_year' ? 'This year'
                    : period === 'previous_year' ? 'Last year'
                    : 'Lifetime';
  const customers = referrers?.top_customers || [];

  return (
    <section className="max-w-4xl mx-auto px-5 sm:px-8 py-6 sm:py-10" data-testid="referrers-section">
      <SectionEyebrow>Word-of-mouth engine</SectionEyebrow>
      <h2 className="text-2xl sm:text-4xl leading-tight max-w-2xl" style={{ fontFamily: SERIF, fontWeight: 500 }}>
        Referrers — {periodLabel.toLowerCase()}
      </h2>
      <p className="mt-4 text-[15px] leading-[1.7] max-w-2xl" style={{ color: GRAY }}>
        Pollen Dots&apos; growth is 100% organic. Below is the live, ranked list of every customer
        who has referred at least one contact to us — anonymised to initials so individual
        relationships stay private while the leaderboard stays honest. Many of these referred
        contacts have already travelled with us; others are in the planning or booked stage and
        will start contributing once their tours complete.
      </p>

      {customers.length === 0 ? (
        <div className="mt-7 rounded-[28px] border bg-white px-6 sm:px-10 py-10 text-center"
             style={{ borderColor: '#EBDFD6' }} data-testid="referrers-empty">
          <p className="text-sm" style={{ color: GRAY, fontFamily: MONO, letterSpacing: '0.08em' }}>
            No referrer activity recorded for this period yet.
          </p>
        </div>
      ) : (
        <div className="mt-7 rounded-[28px] border bg-white px-5 sm:px-7 py-6 sm:py-7"
             style={{ borderColor: '#EBDFD6', boxShadow: '0 18px 50px -30px rgba(3,9,34,0.18)' }}
             data-testid="referrers-customers-card">
          <p className="text-[11px] uppercase mb-3" style={{ fontFamily: MONO, letterSpacing: '0.16em', color: MAROON }}>
            Customer referrers — {customers.length}
          </p>
          <div className="overflow-y-auto pr-1" style={{ maxHeight: 480 }} data-testid="referrers-scroll">
            {customers.map((r, i) => (
              <ReferrerRow key={`c-${i}`} rank={i + 1} row={r} periodLabel={periodLabel} />
            ))}
          </div>
        </div>
      )}
      <p className="mt-4 text-[12px] italic max-w-2xl" style={{ fontFamily: SERIF, color: '#9a8a80' }}>
        Names are reduced to initials and contact details are excluded. Revenue and the tour
        count next to each referrer include only tours that have actually completed; contacts
        currently in planning or booked stages are reflected in the &lsquo;referred&rsquo; count
        and will roll into revenue once their tours wrap up.
      </p>
    </section>
  );
};

export default function PublicInvestorPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [net, setNet] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [referrers, setReferrers] = useState(null);
  const [kpiPeriod, setKpiPeriod] = useState('lifetime'); // current_year | previous_year | lifetime
  const [kpiLoading, setKpiLoading] = useState(true);
  const [status, setStatus] = useState('loading'); // loading | ok | missing
  const [reading, setReading] = useState(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${API}/public/investor/${slug}`);
        if (!res.ok) { if (live) setStatus('missing'); return; }
        const d = await res.json();
        if (live) { setData(d); setStatus('ok'); document.title = d.headline || 'Pollen Dots — Investor Brief'; }
      } catch { if (live) setStatus('missing'); }
    })();
    // Live atlas — fetched in parallel, page renders without it.
    (async () => {
      try {
        const res = await fetch(`${API}/public/network`);
        if (res.ok && live) setNet(await res.json());
      } catch { /* atlas is progressive enhancement */ }
    })();
    return () => { live = false; };
  }, [slug]);

  // KPI fetch — re-runs whenever the period tab changes. Default = Lifetime.
  useEffect(() => {
    let live = true;
    (async () => {
      if (live) setKpiLoading(true);
      try {
        const [kpiRes, refRes] = await Promise.all([
          fetch(`${API}/public/investor/kpis/${slug}?period=${kpiPeriod}`),
          fetch(`${API}/public/investor/referrers/${slug}?period=${kpiPeriod}&limit=500`),
        ]);
        if (kpiRes.ok && live) { setKpis(await kpiRes.json()); }
        if (refRes.ok && live) { setReferrers(await refRes.json()); }
      } catch { /* counters degrade gracefully */ }
      finally { if (live) setKpiLoading(false); }
    })();
    return () => { live = false; };
  }, [slug, kpiPeriod]);

  const noteParas = useMemo(
    () => (data?.cover_note || '').split(/\n\n+/).map(s => s.trim()).filter(Boolean),
    [data]
  );

  const pins = useMemo(() => {
    if (!net) return [];
    const placesAll = net.places || [];
    return [
      ...placesAll.filter(p => p.kind !== 'enroute_stop' && p.kind !== 'activity').map(p => ({ ...p, type: 'place' })),
      ...placesAll.filter(p => p.kind === 'enroute_stop').map(p => ({ ...p, type: 'stop' })),
      ...placesAll.filter(p => p.kind === 'activity').map(p => ({ ...p, type: 'activity' })),
      ...(net.hotels || []).map(p => ({ ...p, type: 'hotel' })),
      ...(net.guides || []).map(p => ({ ...p, type: 'guide' })),
      ...(net.drivers || []).map(p => ({ ...p, type: 'driver' })),
    ];
  }, [net]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: PAPER }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: MAROON }} />
      </div>
    );
  }

  if (status === 'missing') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center" style={{ background: PAPER, color: INK }}>
        <img src="/og/pollen-logo.png" alt="Pollen Dots" className="h-12 w-auto mb-2" />
        <h1 className="text-2xl" style={{ fontFamily: SERIF }}>This link is no longer active</h1>
        <p className="text-sm" style={{ color: GRAY }}>Please reach out to the Pollen Dots team for a fresh link.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: PAPER, color: INK }} data-testid="public-investor-page">
      <style>{`
        @keyframes pdRise { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
        .pd-rise { opacity: 0; animation: pdRise .9s cubic-bezier(.22,.61,.36,1) forwards; }
        .pd-doc-card { transition: transform .35s ease, box-shadow .35s ease; }
        .pd-doc-card:hover { transform: translateY(-6px); box-shadow: 0 28px 56px -28px rgba(88,28,43,.4); }
        .pd-doc-card:hover .pd-doc-thumb img { transform: scale(1.03); }
        .pd-doc-thumb img { transition: transform .6s ease; }
        ::selection { background: ${PETAL}; color: ${INK}; }
      `}</style>

      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b px-5 sm:px-8 py-3 flex items-center"
        style={{ background: 'rgba(252,248,247,0.94)', backdropFilter: 'blur(10px)', borderColor: '#EBDFD6' }}>
        <a href="https://pollendots.com" target="_blank" rel="noopener noreferrer">
          <img src="/og/pollen-logo.png" alt="Pollen Dots" className="h-8 w-auto" />
        </a>
        <span className="ml-auto hidden sm:block text-[10px] uppercase"
          style={{ fontFamily: MONO, letterSpacing: '0.3em', color: FOREST }}>
          Travel · Connect · Care
        </span>
      </header>

      {/* ---- HERO ---- */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-10">
        <div className="pd-rise"><SectionEyebrow>Investor Brief · Pollen Dots LLP</SectionEyebrow></div>
        <h1 className="pd-rise text-4xl sm:text-5xl lg:text-6xl leading-[1.12]"
          style={{ fontFamily: SERIF, fontWeight: 500, animationDelay: '.08s' }}
          data-testid="investor-headline">
          {data.headline}
        </h1>
        <div className="pd-rise flex items-center gap-3 mt-5" style={{ animationDelay: '.16s' }}>
          <img src="/og/pollen-logo.png" alt="Pollen Dots" className="h-7 sm:h-8 w-auto" />
        </div>
        {data.investor_name && (
          <div className="pd-rise inline-flex items-center gap-2 mt-6 rounded-full pl-3 pr-4 py-1.5 border"
            style={{ borderColor: PETAL, background: '#fff', animationDelay: '.24s' }}
            data-testid="prepared-for">
            <Petal size={16} color={MAROON} />
            <span className="text-[11px] uppercase" style={{ fontFamily: MONO, letterSpacing: '0.12em', color: MAROON }}>
              Prepared for {data.investor_name}
            </span>
          </div>
        )}
        <div className="pd-rise mt-8 space-y-4 text-[15px] sm:text-base leading-[1.7] max-w-2xl"
          style={{ color: GRAY, animationDelay: '.3s' }} data-testid="cover-note">
          {noteParas.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </section>

      {/* ---- LIVE COUNTERS ---- */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="rounded-[28px] border bg-white px-6 sm:px-10 py-8 sm:py-10"
          style={{ borderColor: '#EBDFD6', boxShadow: '0 18px 50px -30px rgba(3,9,34,0.18)' }}
          data-testid="live-counters">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <SectionEyebrow>Key metrics</SectionEyebrow>
              <h2 className="text-lg sm:text-2xl mt-1" style={{ fontFamily: SERIF, fontWeight: 500 }}>
                Drawn live from the Pollen Dots operations platform.
              </h2>
            </div>
            <PeriodTabs value={kpiPeriod} onChange={setKpiPeriod} />
          </div>

          <KpiGrid kpis={kpis} loading={kpiLoading} />
        </div>
      </section>

      {/* ---- TOP REFERRERS (anonymized) ---- */}
      <ReferrersLeaderboard referrers={referrers} period={kpiPeriod} />

      {/* ---- CONSOLIDATED JOURNEY ATLAS ---- */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-6 sm:py-10" data-testid="atlas-section">
        <SectionEyebrow>Operations footprint</SectionEyebrow>
        <h2 className="text-2xl sm:text-4xl leading-tight max-w-2xl" style={{ fontFamily: SERIF, fontWeight: 500 }}>
          Consolidated journey map
        </h2>
        <p className="mt-4 text-[15px] leading-[1.7] max-w-2xl" style={{ color: GRAY }}>
          All completed journeys, consolidated on a single live map — destinations, partner hotels,
          guides, drivers and en-route stops across our operating region. Select any marker to view
          operator field notes and location details. Highlighted markers indicate
          <span style={{ color: MAROON }}> Pollen Dots of Care</span> — partners verified under our Smiles,
          Cultural Exchange and Sustainability initiatives.
        </p>
        {net ? (
          <>
            <div className="relative mt-7 rounded-[28px] overflow-hidden bg-white"
              style={{ boxShadow: '0 24px 60px -30px rgba(88,28,43,0.35)', border: '1px solid #EBDFD6' }}>
              <PollenDotsMap ref={mapRef} pins={pins} aspect="aspect-[4/5] sm:aspect-[16/10] max-h-[60vh] sm:max-h-[640px]" zoom={4} />
            </div>
            <PollenDotsLegend />
          </>
        ) : (
          <div className="mt-7 rounded-[28px] border flex items-center justify-center h-64 bg-white" style={{ borderColor: '#EBDFD6' }}>
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: MAROON }} />
          </div>
        )}
        <p className="mt-4 text-[13px] italic leading-relaxed max-w-2xl" style={{ fontFamily: SERIF, color: '#9a8a80' }}
          data-testid="atlas-recency-note">
          Note: journey mapping was digitised recently; the map currently reflects data from recent
          tours and is updated continuously as new journeys are completed. This is a Phase 1
          release — additional interactive layers (filters by year, by initiative, traveller
          paths, density heatmaps) are on the product roadmap.
        </p>
      </section>

      {/* ---- DOCUMENTS ---- */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-16" data-testid="document-grid">
        <SectionEyebrow>Documents</SectionEyebrow>
        <h2 className="text-2xl sm:text-4xl leading-tight" style={{ fontFamily: SERIF, fontWeight: 500 }}>
          Pitch decks &amp; financial projection
        </h2>
        <p className="mt-4 text-[15px] leading-[1.7] max-w-2xl" style={{ color: GRAY }}>
          View each document in the browser, or download a copy for your records.
        </p>
        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          {(data.files || []).map((f, i) => (
            <div key={f.id} className="pd-doc-card rounded-[22px] overflow-hidden border bg-white flex flex-col"
              style={{ borderColor: '#EBDFD6', boxShadow: '0 10px 30px -18px rgba(3,9,34,0.2)' }}
              data-testid={`doc-card-${f.id}`}>
              <button onClick={() => setReading(f)} className="pd-doc-thumb block w-full overflow-hidden cursor-pointer"
                data-testid={`doc-thumb-btn-${f.id}`} aria-label={`Read ${f.title}`}>
                <img
                  src={`${API}/public/investor/thumb/${f.id}`}
                  alt={f.title}
                  className="w-full h-44 object-cover object-top"
                  style={{ background: '#F2EAE4' }}
                />
              </button>
              <div className="p-5 flex flex-col flex-1">
                <p className="text-[10px] uppercase mb-2" style={{ fontFamily: MONO, letterSpacing: '0.2em', color: MAROON }}>
                  {String(i + 1).padStart(2, '0')}
                </p>
                <p className="text-lg leading-snug" style={{ fontFamily: SERIF, fontWeight: 500 }}>{f.title}</p>
                <p className="text-[11px] mt-2" style={{ fontFamily: MONO, color: '#A99C93' }}>
                  {f.page_count} pages · {fmtSize(f.size)}
                </p>
                <div className="mt-auto pt-5 flex items-center gap-2">
                  <MonoBtn filled onClick={() => setReading(f)} testid={`read-btn-${f.id}`}>
                    Read <ArrowRight className="w-3.5 h-3.5" />
                  </MonoBtn>
                  <a
                    href={`${API}/public/investor/${slug}/file/${f.id}/download`}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-dashed transition-colors hover:bg-[#FCF8F7]"
                    style={{ borderColor: INK, color: FOREST }}
                    aria-label={`Download ${f.title}`}
                    data-testid={`download-btn-${f.id}`}
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- CLOSING / FOOTER ---- */}
      <footer style={{ background: FOREST }} data-testid="investor-footer">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16 text-center">
          <div className="flex justify-center mb-5"><Petal size={30} color={PETAL} /></div>
          <p className="text-2xl sm:text-3xl leading-snug max-w-xl mx-auto" style={{ fontFamily: SERIF, fontWeight: 400, color: '#FCF8F7' }}>
            Care-based, responsible travel across India.
          </p>
          <div className="mt-7">
            <a href="https://pollendots.com" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-[11px] uppercase transition-transform duration-300 hover:-translate-y-0.5"
              style={{ fontFamily: MONO, letterSpacing: '0.12em', background: PAPER, color: FOREST }}
              data-testid="footer-website-link">
              pollendots.com <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
          <p className="mt-8 text-[11px] leading-relaxed" style={{ fontFamily: MONO, color: 'rgba(252,248,247,0.55)' }}>
            Pollen Dots LLP · Pollen Dots is the brand · The Caravan Network is a vertical of Pollen Dots
          </p>
          <p className="mt-2 text-[10px] uppercase" style={{ fontFamily: MONO, letterSpacing: '0.3em', color: 'rgba(252,248,247,0.4)' }}>
            Travel · Connect · Care
          </p>
        </div>
      </footer>

      {reading && <PdfReader file={reading} slug={slug} onClose={() => setReading(null)} />}
    </div>
  );
}
